import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import useFundsTransferStore from "../../../stores/fundsTransferStore";
import {
  useGetDisbursement,
  merchantQueryKeys,
} from "../../../hooks/queries/useMerchantQueries";
import { TransactionQueryKeys } from "../../../hooks/queries/useTransactionQueries";
import {
  POLL_TIMEOUT_MS,
  TERMINAL_DISBURSEMENT_STATUSES,
} from "../config/fundsTransferConfig";

export function usePollDisbursement() {
  const queryClient = useQueryClient();
  const { transactionId } = useFundsTransferStore(
    useShallow((state) => ({ transactionId: state.transactionId })),
  );

  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef(null);

  const { data: pollData } = useGetDisbursement(
    isPolling ? transactionId : null,
  );

  const status = pollData?.status;
  const pollDataRef = useRef(pollData);
  pollDataRef.current = pollData;

  useEffect(() => {
    if (!status) return;
    if (!TERMINAL_DISBURSEMENT_STATUSES.has(status)) return;
    clearTimeout(timeoutRef.current);
    setIsPolling(false);
    useFundsTransferStore.getState().setDisbursementResult(pollDataRef.current);
    useFundsTransferStore.getState().setCurrentStep(4);
    queryClient.invalidateQueries({ queryKey: merchantQueryKeys.merchantBalance() });
    queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
  }, [status, queryClient]);

  useEffect(() => {
    if (!isPolling) return;
    timeoutRef.current = setTimeout(() => {
      setIsPolling(false);
      useFundsTransferStore.getState().setCurrentStep(4);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [isPolling]);

  const startPolling = () => {
    if (!useFundsTransferStore.getState().transactionId) return;
    setIsPolling(true);
  };

  return { isPolling, startPolling };
}
